import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { ReactConfetti } from "./react-confetti.js";
import { CountUp } from "./index.js";
import { Color } from "../common/enums/colors.js";
import { TickerBar } from "./ticker-bar.js";
import { formatIntlUSD } from "./utils.js";
import '../common/ticker-wrapper.css';function useMilestonesTimer(config) {
  const hideMs = config.hideMs ?? 1e4;
  const showMs = config.showMs ?? 6e4;
  const { goalAmount, currentAmount, initialState } = config;
  const progress = Math.floor(currentAmount / goalAmount * 100);
  const timer = useRef(null);
  const currentInitialState = useRef(null);
  const [showMilestones, setShowMilestones] = useState(initialState);
  const currentTimer = timer.current;
  const clearTimer = useCallback(() => {
    if (currentTimer) {
      clearTimeout(currentTimer);
      timer.current = null;
    }
  }, [currentTimer]);
  const doHideMilestones = useCallback(() => {
    clearTimer();
    setShowMilestones(false);
  }, [clearTimer]);
  const setHideTimer = useCallback(() => {
    timer.current = setTimeout(() => {
      doHideMilestones();
    }, hideMs);
  }, [doHideMilestones, hideMs]);
  const doShowMilestones = useCallback(() => {
    clearTimer();
    setShowMilestones(true);
    setHideTimer();
  }, [clearTimer, setHideTimer]);
  const setShowTimer = useCallback(() => {
    timer.current = setTimeout(() => {
      doShowMilestones();
    }, showMs);
  }, [doShowMilestones, showMs]);
  useEffect(() => {
    if (progress < 100 && !currentTimer) {
      if (!showMilestones) setShowTimer();
      else setHideTimer();
    } else if (currentTimer && progress >= 100) {
      clearTimer();
    }
    return () => {
      clearTimer();
    };
  }, [
    clearTimer,
    currentTimer,
    progress,
    setHideTimer,
    setShowTimer,
    showMilestones
  ]);
  useEffect(() => {
    if (initialState === void 0) return () => {
    };
    if (currentInitialState.current !== null) {
      const resetTimer = () => {
        if (timer.current) {
          clearTimeout(timer.current);
          timer.current = null;
        }
      };
      if (currentInitialState.current && !initialState) {
        resetTimer();
        doHideMilestones();
      } else if (!currentInitialState.current && initialState) {
        resetTimer();
        doShowMilestones();
      }
    }
    currentInitialState.current = initialState;
    return () => {
    };
  }, [doHideMilestones, doShowMilestones, initialState]);
  return showMilestones;
}
const BaseProgressBar = (props) => {
  const {
    backgroundColor,
    goalAmount,
    currentAmount,
    width,
    height,
    children,
    showMilestones,
    ccamMode
  } = props;
  const [isCompleted, setIsCompleted] = useState(false);
  const [didMount, setDidMount] = useState(false);
  const percent = useMemo(() => {
    return Math.floor(currentAmount / goalAmount * 100);
  }, [currentAmount, goalAmount]);
  const progress = useMemo(() => {
    return percent >= 100 ? 100 : percent;
  }, [percent]);
  const checkComplete = useCallback(() => {
    setIsCompleted(true);
    const handle = setTimeout(() => {
      setIsCompleted(false);
      setTimeout(checkComplete, 6e5);
      return () => {
        clearTimeout(handle);
      };
    }, 15e3);
  }, []);
  useEffect(() => {
    if (progress >= 100) {
      checkComplete();
    } else if (progress < 100 && isCompleted) {
      setIsCompleted(false);
    }
  }, [checkComplete, isCompleted, progress]);
  useEffect(() => {
    setDidMount(true);
    return () => setDidMount(false);
  }, []);
  if (!didMount) return null;
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `therm-container ${showMilestones ? "active" : "deactive"}`,
      id: "therm",
      children: [
        children,
        /* @__PURE__ */ jsxs("div", { children: [
          progress >= 100 && /* @__PURE__ */ jsx(
            ReactConfetti,
            {
              className: "confetti",
              gravity: 0.3,
              height,
              numberOfPieces: 50,
              width,
              colors: [
                "#8332a7",
                "#1874dc",
                "#64d0e4",
                "#c0da07",
                "#ffc32c",
                "#490e67",
                "#002856",
                "#1eadc1",
                "#75bc21",
                "#ffb125",
                "#c10f3a"
              ]
            }
          ),
          isCompleted ? /* @__PURE__ */ jsx("div", { className: "therm-container__completed", children: /* @__PURE__ */ jsxs("div", { className: "therm-container__completed__goal", children: [
            /* @__PURE__ */ jsx("span", { children: "G" }),
            /* @__PURE__ */ jsx("span", { children: "O" }),
            /* @__PURE__ */ jsx("span", { children: "A" }),
            /* @__PURE__ */ jsx("span", { children: "L" }),
            /* @__PURE__ */ jsx("span", { className: "completed_space", children: "M" }),
            /* @__PURE__ */ jsx("span", { children: "E" }),
            /* @__PURE__ */ jsx("span", { children: "T" })
          ] }) }) : null
        ] }),
        /* @__PURE__ */ jsxs("p", { className: "therm-container__raised", children: [
          "$",
          /* @__PURE__ */ jsx(
            CountUp,
            {
              preserveValue: true,
              decimals: currentAmount - Math.floor(currentAmount) !== 0 ? 2 : 0,
              delay: 0,
              duration: 1,
              end: currentAmount,
              separator: ",",
              useEasing: false
            }
          ),
          " ",
          "Raised"
        ] }),
        /* @__PURE__ */ jsx(
          "div",
          {
            id: "therm-container__expand",
            className: `
            ${progress <= 0 ? "therm-container__inner no-point" : "therm-container__inner point"}
            ${ccamMode ? Color.LIGHT_YELLOW : backgroundColor}
            ${progress >= 80 ? "m-higher" : ""}
          `,
            style: {
              width: `
              ${showMilestones ? 100 : progress}%
            `
            }
          }
        ),
        /* @__PURE__ */ jsxs("p", { className: "therm-container__goal", children: [
          "Goal: ",
          formatter.format(goalAmount).replace(".00", "")
        ] })
      ]
    }
  );
};
const TickerWrapper = ({
  donations,
  rewards,
  targets,
  showScroller,
  showRewards,
  showChallenges,
  showDonors,
  campaignName,
  textColor,
  scrollerSpeed,
  className
}) => {
  const truncateDonorName = useCallback((donorName) => {
    if (donorName.trim().length >= 25) {
      return `${donorName.trim().slice(0, 22)}...`;
    }
    return donorName;
  }, []);
  const donationSection = useMemo(() => {
    return {
      title: "Recent Donations",
      id: "recentDonations",
      items: (donations ?? []).map((donation) => {
        return {
          id: donation.id,
          content: /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("span", { className: "mr-1.5", children: truncateDonorName(donation.donor_name) }),
            /* @__PURE__ */ jsx("span", { children: formatIntlUSD(
              donation.amount.value,
              Number(donation.amount.value) % 1 === 0
            ) })
          ] })
        };
      })
    };
  }, [donations, truncateDonorName]);
  const rewardsSection = useMemo(() => {
    return {
      title: "Rewards",
      id: "rewards",
      items: (rewards ?? []).map((reward) => {
        return {
          id: reward.id,
          content: /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("span", { className: "mr-1.5", children: reward.name }),
            /* @__PURE__ */ jsx("span", { children: formatIntlUSD(
              reward.amount.value,
              Number(reward.amount.value) % 1 === 0
            ) })
          ] })
        };
      })
    };
  }, [rewards]);
  const targetsSection = useMemo(() => {
    return {
      title: "Targets",
      id: "targets",
      items: (targets ?? []).map((target) => {
        return {
          id: target.id,
          content: /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("span", { className: "mr-1.5", children: target.name }),
            /* @__PURE__ */ jsx("span", { children: formatIntlUSD(
              target.amount.value,
              Number(target.amount.value) % 1 === 0
            ) })
          ] })
        };
      })
    };
  }, [targets]);
  const tickerBarSections = [];
  if (showScroller) {
    if (showDonors) tickerBarSections.push(donationSection);
    if (showRewards) tickerBarSections.push(rewardsSection);
    if (showChallenges) tickerBarSections.push(targetsSection);
    if (campaignName)
      tickerBarSections.push({
        title: "Campaign",
        id: "campaign",
        items: [
          {
            id: "campaignName",
            content: campaignName
          }
        ]
      });
    if (className)
      return /* @__PURE__ */ jsx("div", { className, children: /* @__PURE__ */ jsx(
        TickerBar,
        {
          color: Color.DARK_GREY,
          sections: tickerBarSections,
          speed: scrollerSpeed,
          textColor
        }
      ) });
    return /* @__PURE__ */ jsx(
      TickerBar,
      {
        color: Color.DARK_GREY,
        sections: tickerBarSections,
        speed: scrollerSpeed,
        textColor
      }
    );
  }
};
export {
  BaseProgressBar,
  TickerWrapper,
  useMilestonesTimer
};
//# sourceMappingURL=ticker-wrapper.js.map
